import { ArrowTopRightOnSquareIcon } from '@heroicons/react/16/solid';

import { CompanionLabel } from '@/components/common/CompanionLabel';
import { TableColumn } from '@/components/common/DataTable';
import { RainmakerLogo } from '@/components/rainmaker';
import { PriorityPill, TaskStatusPill } from '@/components/tasks/shared';
import { AssigneeAvatar } from '@/components/tasks-datatable/components/AssigneeAvatar';
import { TaskAgentStatusCell } from '@/components/tasks-datatable/components/TaskAgentStatusCell';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { TaskFilterOption } from '@/services/api/tasksService';
import { Task } from '@/services/types/client-intake-types';
import { TaskExternalCMSUpdateStatus } from '@/services/types/task-types';
import { formatDateInNY, getDateInNY, getToday } from '@/utils/dateUtils';
import { formatRelativeTime } from '@/utils/taskDateUtils';

import { COLUMN_CATEGORIES } from '../config/columnConfig';
import {
  FILTER_CONFIG_KEYS,
  TableColumnIds,
  TASK_FILTERS_CONFIG,
} from '../config/filterConfig';
import {
  getActiveDueDatePreset,
  getActiveSlaResolutionPreset,
  getPresetDateValues,
  getPresetSlaResolutionValues,
} from '../filterPresets';
import { getSortedOptions } from '../utils/filterUtils';

// --- Standard Column Definitions ---

export const createPriorityColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => ({
  id: TableColumnIds.PRIORITY,
  header: 'Priority',
  accessorKey: 'order',
  width: '100px',
  align: 'left' as const,
  sortable: true,
  hideable: true,
  category: COLUMN_CATEGORIES.SPECIAL,
  sort: {
    labels: {
      asc: 'Highest Priority First',
      desc: 'Lowest Priority First',
    },
  },
  cell: (row) => {
    // Use backend computed priority (SINGLE SOURCE OF TRUTH)
    // This ensures the displayed priority matches the sort order exactly.
    // The backend guarantees this field is present in list views.
    // If missing (e.g. sparse update), we fall back to 'normal' to be safe.
    const priority = row.priority_label || 'normal';
    return <PriorityPill priority={priority} />;
  },
  filter: options
    ? {
        type: 'multiselect',
        options: getSortedOptions(FILTER_CONFIG_KEYS.PRIORITY, options),
        searchable: false,
      }
    : undefined,
});

export const createMatterColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => ({
  id: TableColumnIds.MATTER_NAME,
  header: 'Matter',
  accessorKey: 'matter_name',
  width: '230px',
  sortable: true,
  hideable: true,
  category: COLUMN_CATEGORIES.MATTER,
  sort: {
    labels: {
      asc: 'A to Z',
      desc: 'Z to A',
    },
  },
  cell: (row) => (
    <div className="flex items-center gap-2 min-w-0">
      <a
        href={`/matters/${row.matter}/?task_id=${row.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 min-w-0 text-black dark:text-white truncate hover:underline"
        title={row.matter_name}
        onClick={(e) => e.stopPropagation()}
      >
        {row.matter_name}
      </a>
      <CompanionLabel
        memberCount={row.companion_count ?? 0}
        isPrimary={row.is_companion_primary ?? false}
        size="sm"
      />
    </div>
  ),
  filter: options
    ? {
        type: 'multiselect',
        options: getSortedOptions(FILTER_CONFIG_KEYS.MATTER, options),
        searchable: true,
        selectAllEnabled:
          TASK_FILTERS_CONFIG[FILTER_CONFIG_KEYS.MATTER].selectAllEnabled,
      }
    : undefined,
});

export const createCaseStatusColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => ({
  id: TableColumnIds.CASE_STATUS,
  header: 'Case Status',
  accessorKey: 'matter_status',
  width: '160px',
  sortable: true,
  hideable: true,
  category: COLUMN_CATEGORIES.MATTER,
  cell: (row, value) => {
    if (!value) return <span className="text-gray-400 text-sm">—</span>;
    return (
      <span
        className="text-gray-700 dark:text-zinc-300 truncate"
        title={value as string}
      >
        {value as string}
      </span>
    );
  },
  filter: options
    ? {
        type: 'multiselect',
        options: getSortedOptions(FILTER_CONFIG_KEYS.MATTER_STATUS, options),
        searchable: true,
      }
    : undefined,
});

export const createTaskTypeColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => ({
  id: TableColumnIds.TASK_TYPE,
  header: 'Task Type',
  accessorKey: 'type',
  width: '150px',
  sortable: true,
  resizable: true,
  hideable: true,
  category: COLUMN_CATEGORIES.TASK_INFO,
  sort: {
    sortKey: 'type__name', // Backend expects type__name for sorting
    labels: {
      asc: 'A to Z',
      desc: 'Z to A',
    },
  },
  cell: (row) => {
    const taskType = row.type?.name;

    return taskType ? (
      <span className="text-black dark:text-white truncate" title={taskType}>
        {taskType}
      </span>
    ) : (
      <span className="text-gray-400">—</span>
    );
  },
  filter: options
    ? {
        type: 'multiselect',
        options: getSortedOptions(FILTER_CONFIG_KEYS.CANONICAL_TYPES, options),
        searchable: true,
      }
    : undefined,
});

export const createTaskNameColumn = (): TableColumn<Task> => ({
  id: TableColumnIds.TASK_NAME,
  header: 'Task Name',
  accessorKey: 'name',
  width: '250px',
  sortable: true,
  resizable: true,
  hideable: true,
  category: COLUMN_CATEGORIES.TASK_INFO,
  sort: {
    sortKey: 'name', // Backend expects 'name' for sorting (not 'task_name')
    labels: {
      asc: 'A to Z',
      desc: 'Z to A',
    },
  },
  cell: (row) => {
    const { name } = row;

    return name ? (
      <span className="text-black dark:text-white truncate" title={name}>
        {name}
      </span>
    ) : (
      <span className="text-gray-400">—</span>
    );
  },
});

export const createTaskDescriptionColumn = (): TableColumn<Task> => ({
  id: TableColumnIds.TASK_DESCRIPTION,
  header: 'Task Description',
  accessorKey: 'description',
  width: '250px',
  resizable: true,
  hideable: true,
  category: COLUMN_CATEGORIES.TASK_INFO,
  cell: (row) => {
    const { description } = row;

    return description ? (
      <span className="text-black dark:text-white truncate" title={description}>
        {description}
      </span>
    ) : (
      <span className="text-gray-400">—</span>
    );
  },
});

// MP sentinel values - these appear as presets only, not checkbox options
const MP_ASSIGNEE_SENTINELS = new Set([
  'managing_paralegals',
  'exclude_managing_paralegals',
]);

export const createAssigneeColumn = (
  options?: TaskFilterOption[],
  currentUserId?: string,
): TableColumn<Task> => {
  // Filter out MP sentinel values from checkbox options (they appear as presets instead)
  const userOptions = options?.filter(
    (opt) => !MP_ASSIGNEE_SENTINELS.has(opt.value),
  );

  return {
    id: TableColumnIds.ASSIGNEE,
    header: 'Assignee',
    accessorKey: 'assignee',
    width: '80px',
    align: 'center' as const,
    hideable: true,
    category: COLUMN_CATEGORIES.PEOPLE,
    cell: (row) => {
      const { assignee } = row;
      if (!assignee) {
        return <span className="text-gray-400 text-sm">—</span>;
      }
      return (
        <AssigneeAvatar
          firstName={assignee.first_name}
          lastName={assignee.last_name}
        />
      );
    },
    filter: userOptions
      ? {
          type: 'multiselect',
          options: getSortedOptions(FILTER_CONFIG_KEYS.ASSIGNEE, userOptions),
          searchable: true,
          selectAllEnabled:
            TASK_FILTERS_CONFIG[FILTER_CONFIG_KEYS.ASSIGNEE].selectAllEnabled,
          // Quick filter presets: "Mine", "Unassigned", and "MPs" filters
          presets: [
            // "Mine" only available if we have current user ID
            ...(currentUserId
              ? [{ name: 'Mine', values: [currentUserId] }]
              : []),
            // "Unassigned" always available
            { name: 'Unassigned', values: ['unassigned'] },
            // Managing Paralegal presets (backend sentinel values)
            { name: 'MPs', values: ['managing_paralegals'] },
            { name: 'Exclude MPs', values: ['exclude_managing_paralegals'] },
          ],
        }
      : undefined,
  };
};

export const createLastUpdateColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => ({
  id: TableColumnIds.LAST_UPDATE,
  header: 'Last Update At',
  accessorKey: 'latest_update_at',
  sortable: true,
  width: '140px',
  headerTooltip: 'Most recent task update with count',
  hideable: true,
  category: COLUMN_CATEGORIES.DATES,
  filter: options
    ? {
        type: 'multiselect',
        options: getSortedOptions(
          FILTER_CONFIG_KEYS.LAST_UPDATE_RANGE,
          options,
        ),
        searchable: false,
      }
    : undefined,
  sort: {
    labels: {
      asc: 'Oldest Updates First',
      desc: 'Newest Updates First',
    },
    // Default arrows: ↓ = desc = Newest (intuitive for "most recent")
  },
  cell: (row) => {
    const timestamp = row.latest_update_at as string | null | undefined;
    const count = row.updates_count as number | undefined;

    // No updates yet
    if (!timestamp || !count || count === 0) {
      return <span className="text-gray-400">—</span>;
    }

    // Format relative time using existing utility
    const relativeTime = formatRelativeTime(timestamp);

    // Full timestamp for tooltip
    const fullTimestamp = new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 cursor-help">
            {/* Relative time */}
            <span className="text-black dark:text-white">{relativeTime}</span>

            {/* Comment icon and count */}
            {count >= 1 && (
              <span className="inline-flex items-center gap-1 text-gray-700 dark:text-zinc-300">
                <span>💬</span>
                <span>{count}</span>
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">{fullTimestamp}</p>
        </TooltipContent>
      </Tooltip>
    );
  },
});

export const createLastUpdateActorColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => {
  // Backend returns:
  // - user:<uuid> for individual humans
  // - agent:<type> for system/agent actors
  //
  // For UX + performance, we collapse all human users into a single
  // "Human" option that maps to the backend `last_update_actor=human`
  // semantic filter, and keep the distinct agent options.
  const hasHumanUsers =
    options?.some((opt) => opt.value.startsWith('user:')) ?? false;

  const agentOptions: TaskFilterOption[] =
    options?.filter((opt) => opt.value.startsWith('agent:')) ?? [];

  const lastUpdateActorOptions: TaskFilterOption[] = [
    ...(hasHumanUsers ? [{ label: 'Human', value: 'human' }] : []),
    ...agentOptions,
  ];

  return {
    id: TableColumnIds.LAST_UPDATE_ACTOR,
    header: 'Last Updated By',
    accessorKey: 'latest_update_actor_label',
    width: '140px',
    headerTooltip: 'Who made the most recent update (Human or Agent)',
    hideable: true,
    category: COLUMN_CATEGORIES.PEOPLE,
    filter:
      lastUpdateActorOptions.length > 0
        ? {
            type: 'multiselect',
            options: getSortedOptions(
              FILTER_CONFIG_KEYS.LAST_UPDATE_ACTOR,
              lastUpdateActorOptions,
            ),
            searchable: true,
            // Quick filter preset: "Human" applies the backend semantic
            // filter value `last_update_actor=human` (any human user).
            presets: hasHumanUsers
              ? [{ name: 'Human', values: ['human'] }]
              : [],
          }
        : undefined,
    cell: (row) => {
      const actorLabel = row.latest_update_actor_label as
        | string
        | null
        | undefined;

      if (!actorLabel) {
        return <span className="text-gray-400">—</span>;
      }

      return (
        <span
          className="text-gray-700 dark:text-zinc-300 truncate"
          title={actorLabel}
        >
          {actorLabel}
        </span>
      );
    },
  };
};

export const createLastUpdateTextColumn = (): TableColumn<Task> => ({
  id: TableColumnIds.LAST_UPDATE_TEXT,
  header: 'Last Update',
  accessorKey: 'latest_update_text',
  width: '200px',
  resizable: true,
  hideable: true,
  category: COLUMN_CATEGORIES.DATES,
  cell: (row, value) => {
    const text = value as string | null | undefined;

    if (!text) {
      return <span className="text-gray-400">—</span>;
    }

    // Truncate to ~80 chars for display, show full text in title tooltip
    const displayText = text.length > 80 ? `${text.slice(0, 80)}...` : text;

    return (
      <span className="text-gray-700 dark:text-zinc-300 truncate" title={text}>
        {displayText}
      </span>
    );
  },
});

export const createCreatedAtColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => ({
  id: TableColumnIds.CREATED_AT,
  header: 'Created',
  accessorKey: 'created_at',
  sortable: true,
  width: '140px',
  hideable: true,
  category: COLUMN_CATEGORIES.DATES,
  filter: options
    ? {
        type: 'multiselect',
        options: getSortedOptions(FILTER_CONFIG_KEYS.CREATED_AT_RANGE, options),
        searchable: false,
      }
    : undefined,
  sort: {
    labels: {
      asc: 'Oldest First',
      desc: 'Newest First',
    },
  },
  cell: (row) => {
    const timestamp = row.created_at as string | null | undefined;

    if (!timestamp) {
      return <span className="text-gray-400">—</span>;
    }

    const relativeTime = formatRelativeTime(timestamp);
    const fullTimestamp = new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return (
      <span className="text-black dark:text-white" title={fullTimestamp}>
        {relativeTime}
      </span>
    );
  },
});

export const createDueDateColumn = (): TableColumn<Task> => ({
  id: TableColumnIds.DUE_DATE,
  header: 'Next Action Date',
  accessorKey: 'due_date',
  sortable: true,
  width: '140px',
  hideable: true,
  category: COLUMN_CATEGORIES.DATES,
  sort: {
    labels: {
      asc: 'Earliest Next Action Date First',
      desc: 'Latest Next Action Date First',
    },
    invertArrows: true, // ↓ = Earliest (closest to now, intuitive for due dates)
  },
  cell: (row, value) => {
    if (!value) return <span className="text-gray-400">—</span>;

    // Format and compare in NY timezone to match backend filter behavior
    const formattedDate = formatDateInNY(value as string);
    const dateNY = getDateInNY(value as string);
    const todayNY = getToday();
    const isOverdue = dateNY < todayNY;
    const textColor = isOverdue ? 'text-red-600' : 'text-black dark:text-white';

    return (
      <span className={textColor} title={formattedDate}>
        {formattedDate}
      </span>
    );
  },
  filter: {
    type: 'daterange',
    searchable: false,
    // Presets are computed by frontend - values MUST match DUE_DATE_PRESET_IDS
    // from filterPresets/dueDatePresets.ts for active state detection to work
    presets: [
      { name: 'Before Today', values: ['before_today'] },
      { name: 'Today', values: ['today'] },
      { name: 'None', values: ['none'] },
    ],
    dateRangeConfig: {
      startKey: FILTER_CONFIG_KEYS.DUE_DATE_AFTER,
      endKey: FILTER_CONFIG_KEYS.DUE_DATE_BEFORE,
      noDueDateKey: FILTER_CONFIG_KEYS.NO_DUE_DATE,
      startLabel: 'From',
      endLabel: 'To',
      // Preset handlers from tasks-datatable/filterPresets
      getActivePreset: getActiveDueDatePreset,
      getPresetValues: getPresetDateValues,
    },
  },
});

export const createSlaResolutionDateColumn = (): TableColumn<Task> => ({
  id: TableColumnIds.SLA_RESOLUTION_DATE,
  header: 'Due Date',
  accessorKey: 'sla_resolution_target',
  sortable: true,
  width: '120px',
  hideable: true,
  category: COLUMN_CATEGORIES.DATES,
  sort: {
    labels: {
      asc: 'Earliest Due Date First',
      desc: 'Latest Due Date First',
    },
    invertArrows: true, // ↓ = Earliest (closest to now, intuitive for due dates)
  },
  cell: (row, value) => {
    if (!value) return <span className="text-gray-400">—</span>;

    // Format and compare in NY timezone to match backend filter behavior
    const formattedDate = formatDateInNY(value as string);
    const dateNY = getDateInNY(value as string);
    const todayNY = getToday();
    const isOverdue = dateNY < todayNY;
    const textColor = isOverdue ? 'text-red-600' : 'text-black dark:text-white';

    return (
      <span className={textColor} title={formattedDate}>
        {formattedDate}
      </span>
    );
  },
  filter: {
    type: 'daterange',
    searchable: false,
    presets: [
      { name: 'Before Today', values: ['before_today'] },
      { name: 'Today', values: ['today'] },
      { name: 'None', values: ['none'] },
    ],
    dateRangeConfig: {
      startKey: FILTER_CONFIG_KEYS.SLA_RESOLUTION_TARGET_AFTER,
      endKey: FILTER_CONFIG_KEYS.SLA_RESOLUTION_TARGET_BEFORE,
      noDueDateKey: FILTER_CONFIG_KEYS.NO_SLA_RESOLUTION_TARGET,
      startLabel: 'From',
      endLabel: 'To',
      getActivePreset: getActiveSlaResolutionPreset,
      getPresetValues: getPresetSlaResolutionValues,
    },
  },
});

export const createTaskStatusColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => ({
  id: TableColumnIds.TASK_STATUS,
  header: 'Task Status',
  accessorKey: 'status',
  width: '120px',
  hideable: true,
  category: COLUMN_CATEGORIES.TASK_INFO,
  cell: (row) => (
    <TaskStatusPill status={row.status?.canonical_status as string} />
  ),
  filter:
    options && options.length > 0
      ? {
          type: 'multiselect',
          options: getSortedOptions(FILTER_CONFIG_KEYS.STATUS, options),
          searchable: true,
          presets: [
            {
              name: 'Active',
              values: ['Pending', 'In Progress', 'Blocked'],
            },
          ],
        }
      : undefined,
});

export const createAgentColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => ({
  id: TableColumnIds.AGENT,
  header: 'Agent',
  accessorKey: 'crashing_out_agent_status',
  width: '88px',
  align: 'center',
  sortable: true,
  sort: {
    sortKey: 'crashing_out_agent_status_sort',
  },
  hideable: true,
  category: COLUMN_CATEGORIES.TASK_INFO,
  cell: (row) => <TaskAgentStatusCell task={row} />,
  filter: options
    ? {
        type: 'multiselect',
        options: getSortedOptions(FILTER_CONFIG_KEYS.AGENT_STATUS, options),
        searchable: false,
      }
    : undefined,
});

export const createIncidentColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => ({
  id: TableColumnIds.INCIDENT,
  header: 'Incident',
  accessorKey: 'incident_type_display',
  width: '150px',
  sortable: true,
  hideable: true,
  category: COLUMN_CATEGORIES.MATTER,
  cell: (row, value) => {
    const incidentType = (value as string) || '—';
    return (
      <span
        className="text-gray-700 dark:text-zinc-300 truncate"
        title={incidentType}
      >
        {incidentType}
      </span>
    );
  },
  filter: options
    ? {
        type: 'multiselect',
        options: getSortedOptions(FILTER_CONFIG_KEYS.INCIDENT_TYPE, options),
        searchable: true,
      }
    : undefined,
});

export const createFirmColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => ({
  id: TableColumnIds.FIRM_NAME,
  header: 'Firm',
  accessorKey: 'firm_name',
  width: '140px',
  sortable: true,
  hideable: true,
  category: COLUMN_CATEGORIES.MATTER,
  clickable: {
    getHref: (row) => `/firms/${row.firm}`,
    target: '_blank',
  },
  filter: options
    ? {
        type: 'multiselect',
        options: getSortedOptions(FILTER_CONFIG_KEYS.MATTER_FIRM, options),
        searchable: true,
      }
    : undefined,
});

export const createFirmPrimaryStateColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => ({
  id: TableColumnIds.FIRM_PRIMARY_STATE,
  header: 'Firm State',
  accessorKey: 'firm_primary_state',
  width: '120px',
  sortable: true,
  hideable: true,
  category: COLUMN_CATEGORIES.MATTER,
  cell: (row, value) => (
    <span
      className="text-gray-700 dark:text-zinc-300 truncate"
      title={(value as string) || ''}
    >
      {(value as string) || '—'}
    </span>
  ),
  filter: options
    ? {
        type: 'multiselect',
        options: getSortedOptions(FILTER_CONFIG_KEYS.FIRM_STATE, options),
        searchable: true,
      }
    : undefined,
});

export const createCoverageColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => ({
  id: TableColumnIds.COVERAGE,
  header: 'Coverage',
  accessorKey: 'coverage',
  width: '180px',
  hideable: true,
  category: COLUMN_CATEGORIES.MATTER,
  cell: (row) => {
    const coverageName =
      row.coverage_carrier_name || row.coverage?.carrier?.name || '—';
    return (
      <span
        className="text-gray-700 dark:text-zinc-300 truncate"
        title={coverageName}
      >
        {coverageName}
      </span>
    );
  },
  filter: options
    ? {
        type: 'multiselect',
        options: getSortedOptions(FILTER_CONFIG_KEYS.COVERAGE_CARRIER, options),
        searchable: true,
      }
    : undefined,
});

export const createCmsStatusColumn = (): TableColumn<Task> => ({
  id: TableColumnIds.CMS_STATUS,
  header: 'CMS Status',
  accessorKey: 'external_cms_update_status',
  width: '120px',
  hideable: true,
  category: COLUMN_CATEGORIES.SPECIAL,
  cell: (row) => {
    const status = row.external_cms_update_status;
    if (status === TaskExternalCMSUpdateStatus.NOT_UPDATED) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              Needs Update
            </span>
          </TooltipTrigger>
          {row.external_cms_update_reason && (
            <TooltipContent>
              <p className="text-xs">{row.external_cms_update_reason}</p>
            </TooltipContent>
          )}
        </Tooltip>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
        Updated
      </span>
    );
  },
  filter: {
    type: 'select',
    options: [
      { label: 'Needs Update', value: TaskExternalCMSUpdateStatus.NOT_UPDATED },
      { label: 'Updated', value: TaskExternalCMSUpdateStatus.UPDATED },
    ],
    searchable: false,
  },
});

export const createSourceColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => ({
  id: TableColumnIds.TASK_SOURCE,
  header: 'Source',
  accessorKey: 'source_display',
  width: '150px',
  hideable: true,
  category: COLUMN_CATEGORIES.SPECIAL,
  cell: (row, value) => {
    const display = (value as string) || row.source || '—';
    const createdByName = row.created_by_name;

    // Show tooltip with creator name for user-created tasks
    if (row.source === 'finch_user' && createdByName) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-gray-700 dark:text-zinc-300 truncate cursor-help">
              {display}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs">Created by: {createdByName}</p>
          </TooltipContent>
        </Tooltip>
      );
    }

    // Show "View Template" link for firm task template tasks
    if (row.source === 'finch_custom_task' && row.firm) {
      return (
        <div className="flex items-center gap-1.5">
          <span
            className="text-gray-700 dark:text-zinc-300 truncate"
            title={display}
          >
            {display}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={`/firms/${row.firm}/services`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              </a>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">View Firm Task Template</p>
            </TooltipContent>
          </Tooltip>
        </div>
      );
    }

    return (
      <span
        className="text-gray-700 dark:text-zinc-300 truncate"
        title={display}
      >
        {display}
      </span>
    );
  },
  filter: options
    ? {
        type: 'multiselect',
        options: getSortedOptions(FILTER_CONFIG_KEYS.SOURCE, options),
        searchable: false,
      }
    : undefined,
});

export const createRainmakerColumn = (): TableColumn<Task> => ({
  id: TableColumnIds.RAINMAKER,
  header: (
    <RainmakerLogo size={16} className="text-violet-500 dark:text-violet-400" />
  ),
  accessorKey: 'has_rainmaker',
  width: '60px',
  align: 'center' as const,
  headerTooltip: 'Filter by Rainmaker',
  hideable: true,
  category: COLUMN_CATEGORIES.SPECIAL,
  cell: (row) => {
    const hasRainmaker = row.has_rainmaker;
    return hasRainmaker ? (
      <div
        className="flex items-center justify-center w-5 h-5 rounded-md bg-gradient-to-br from-violet-500 to-purple-600 shadow-sm"
        title="Rainmaker"
      >
        <RainmakerLogo size={12} className="text-white" />
      </div>
    ) : null;
  },
  filter: {
    type: 'select',
    options: [
      { label: 'Rainmaker', value: 'true' },
      { label: 'Non-Rainmaker', value: 'false' },
    ],
    searchable: false,
  },
});

// --- Standard Column Options Type ---
// Canonical type for all filter options passed to column factories.
// This ensures all verticals receive and pass correct options consistently.
export interface StandardColumnOptions {
  firmOptions?: TaskFilterOption[];
  firmStateOptions?: TaskFilterOption[];
  incidentTypeOptions?: TaskFilterOption[];
  caseStatusOptions?: TaskFilterOption[];
  matterOptions?: TaskFilterOption[];
  coverageOptions?: TaskFilterOption[];
  assigneeOptions?: TaskFilterOption[];
  priorityOptions?: TaskFilterOption[];
  dueDateOptions?: TaskFilterOption[];
  lastUpdateOptions?: TaskFilterOption[];
  lastUpdateActorOptions?: TaskFilterOption[];
  createdDateOptions?: TaskFilterOption[];
  statusOptions?: TaskFilterOption[];
  policeDepartmentOptions?: TaskFilterOption[];
  medicalProviderOptions?: TaskFilterOption[];
  /** Task type options derived from countsData.types (contextual to vertical) */
  taskTypeOptions?: TaskFilterOption[];
  /** Task source options (Finch System, Finch User, External CMS) */
  sourceOptions?: TaskFilterOption[];
  /** Agent status options (Needs Review, Working, Not Running) */
  agentStatusOptions?: TaskFilterOption[];
  /** Current user ID for "Mine" quick filter preset in Assignee column */
  currentUserId?: string;
}

// --- Factory for Standard Columns ---
/**
 * Creates the standard set of columns used by all task verticals.
 *
 * This is the canonical column factory that ensures all filter options
 * are correctly threaded through. Verticals should use this instead of
 * manually composing columns to avoid missing options.
 *
 * @param options - Filter options (from useTaskVerticalView hook)
 * @param insertAfterFirm - Optional vertical-specific columns to insert between Firm and TaskStatus
 */
export const createBaseTaskColumns = (
  options: StandardColumnOptions,
  insertAfterFirm: TableColumn<Task>[] = [],
): TableColumn<Task>[] => [
    createPriorityColumn(options.priorityOptions),
    createDueDateColumn(),
    createSlaResolutionDateColumn(),
    createMatterColumn(options.matterOptions),
    createTaskNameColumn(),
    createTaskDescriptionColumn(),
    createTaskTypeColumn(options.taskTypeOptions),
    createTaskStatusColumn(options.statusOptions),
    createAgentColumn(options.agentStatusOptions),
    createAssigneeColumn(options.assigneeOptions, options.currentUserId),
    createLastUpdateColumn(options.lastUpdateOptions),
    createLastUpdateActorColumn(options.lastUpdateActorOptions),
    createLastUpdateTextColumn(),
    createCreatedAtColumn(options.createdDateOptions),
    createCaseStatusColumn(options.caseStatusOptions),
    createCmsStatusColumn(),
    createIncidentColumn(options.incidentTypeOptions),
    createFirmColumn(options.firmOptions),
    createFirmPrimaryStateColumn(options.firmStateOptions),
    createCoverageColumn(options.coverageOptions),
    ...insertAfterFirm,
    createSourceColumn(options.sourceOptions),
    createRainmakerColumn(),
  ];
